import { STATUS_COLOR } from "@constants/resultColors";
import { EyeType, TestType } from "types/testTypes";
import { getEyeDataForPupillometry } from "../../new_test_ui/new_test_steps/test-configuration/extra-tests/pupillometry/PupillometryCalculations";
import { interpretOcularMotilitySummary } from "../../testContainer/result/utils/ocularMotilityInterpretation";
import { interpretPupillometrySummary } from "../../testContainer/result/utils/pupillometryInterpretation";
import { PupillometryData, OcularMotilityData } from "../hooks/useNeurologicalData";
import { OcularMotilityMetrics } from "../../../types/tests/specifics";

const getPupillometryInterpretation = (data: PupillometryData | null) => {
  if (!data?.chartData?.length) return null;

  return interpretPupillometrySummary({
    testType: data.testType,
    leftResults:
      data.testType === TestType.Pupillometry
        ? getEyeDataForPupillometry(data.chartData, EyeType.LeftEye, "NORMAL")
        : null,
    rightResults:
      data.testType === TestType.Pupillometry
        ? getEyeDataForPupillometry(data.chartData, EyeType.RightEye, "NORMAL")
        : null,
    leftResultsDirect:
      data.testType === TestType.RAPD
        ? getEyeDataForPupillometry(data.chartData, EyeType.LeftEye, "DIRECT")
        : null,
    rightResultsDirect:
      data.testType === TestType.RAPD
        ? getEyeDataForPupillometry(data.chartData, EyeType.RightEye, "DIRECT")
        : null,
    leftResultsConsensual:
      data.testType === TestType.RAPD
        ? getEyeDataForPupillometry(data.chartData, EyeType.LeftEye, "CONSENTUAL")
        : null,
    rightResultsConsensual:
      data.testType === TestType.RAPD
        ? getEyeDataForPupillometry(data.chartData, EyeType.RightEye, "CONSENTUAL")
        : null,
  });
};

const getOcularMotilityInterpretation = (data: OcularMotilityData | null) => {
  if (!data?.metrics) return null;

  return interpretOcularMotilitySummary({
    chartData: data.chartData,
    metrics: data.metrics,
    testType: data.testType,
  });
};

// Pupillometry calculations
export const getPupillometryStatus = (data: PupillometryData | null): string => {
  const interpretation = getPupillometryInterpretation(data);
  if (!interpretation) return "unknown";
  return interpretation.riskLevel === "low" ? "normal" : interpretation.riskLevel;
};

export const getPupillometryStatusColor = (status: string): string => {
  if (status === "normal") return STATUS_COLOR.good;
  if (status === "high") return STATUS_COLOR.bad;
  return STATUS_COLOR.cautionYellow;
};

export const getPupillometryAssessmentKey = (data: PupillometryData | null): string => {
  const interpretation = getPupillometryInterpretation(data);
  if (!interpretation) return "No data available";
  return interpretation.reasonKey;
};

// Ocular Motility calculations
export const getOcularMotilityStatus = (data: OcularMotilityData | null): string => {
  const interpretation = getOcularMotilityInterpretation(data);
  if (!interpretation) return "unknown";
  return interpretation.riskLevel === "low" ? "normal" : interpretation.riskLevel;
};

export const getOcularMotilityStatusColor = (status: string): string => {
  if (status === "normal") return STATUS_COLOR.good;
  if (status === "high") return STATUS_COLOR.bad;
  return STATUS_COLOR.cautionYellow;
};

export const getOcularMotilityAssessmentKey = (data: OcularMotilityData | null): string => {
  const interpretation = getOcularMotilityInterpretation(data);
  if (!interpretation) return "No data available";
  return interpretation.reasonKey;
};

// ─── Ocular Motility Status ───────────────────────────────────────────────────

export const getOcularMotilityStatusFromMetrics = (
  metrics: OcularMotilityMetrics | null | undefined,
): "normal" | "attention" => {
  const interpretation = interpretOcularMotilitySummary({
    chartData: null,
    metrics: metrics ?? null,
    testType: TestType.CircularPursuit,
  });

  return interpretation?.riskLevel === "low" ? "normal" : "attention";
};

// Overall assessment
export const getOverallStatus = (
  pupillometry: PupillometryData | null,
  ocularMotility: OcularMotilityData | null,
): { status: string; color: string } => {
  const pupillometryRisk = getPupillometryInterpretation(pupillometry)?.riskLevel;
  const ocularRisk = getOcularMotilityInterpretation(ocularMotility)?.riskLevel;

  const hasHigh = [pupillometryRisk, ocularRisk].some((status) => status === "high");
  const hasMonitor = [pupillometryRisk, ocularRisk].some((status) => status === "monitor");

  if (hasHigh) return { status: "Attention Required", color: STATUS_COLOR.bad };
  if (hasMonitor) return { status: "Monitor", color: STATUS_COLOR.cautionYellow };
  return { status: "Normal", color: STATUS_COLOR.good };
};
